import {
  ACTIVITY_STATUS,
  DataTable,
  DEFAULT_DEBOUNCE_DELAY,
  ModalActivityDetails,
  STATUSPAGE,
} from '@repo/ui';
import debounce from 'debounce';
import { GetAllActivitiesByStatusResponse } from 'domain/models';
import { GetAllActivitiesByStatus } from 'domain/usecases';
import { ORDER_COLUMN, ORDER_TYPE } from 'domain/value-objects/order-type';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';

import noDataBg from '../../../../../assets/images/no-task.png';
import { ColumnsActivities } from '../../../common/columns/activities-tabs';
import { useDashboard } from '../../../hooks/useDashboard';
import { useFilters } from '../../../hooks/useFilters';
import { IActivitiesTabs } from '../../../models/activities-tabs';

interface IPaginationMetadata {
  selectedPage: number;
  rowsPerPage: number;
  orderColumn: ORDER_COLUMN;
  sortDirection: ORDER_TYPE;
  searchString: string;
}

interface IProps {
  activityStatus: ACTIVITY_STATUS;
}

const TableActivityByStatus = ({ activityStatus }: IProps) => {
  const [activityByStatusData, setActivityByStatusData] =
    useState<GetAllActivitiesByStatusResponse>();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { filters } = useFilters();
  const {
    requestGetActivityByStatus,
    activityByStatus,
    loadingActivityByStatus,
  } = useDashboard({});

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const [paginationMetadata, setPaginationMetadata] =
    useState<IPaginationMetadata>({
      selectedPage: 0,
      rowsPerPage: 10,
      orderColumn: ORDER_COLUMN.STUDENT,
      sortDirection: ORDER_TYPE.ASC,
      searchString: '',
    });

  const changePaginationMetadata = <T extends keyof IPaginationMetadata>(
    key: T,
    value: IPaginationMetadata[T],
  ) => {
    setPaginationMetadata((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };
  const [listStatusPage, setListStatusPage] = useState(STATUSPAGE.LOADING);

  const handleSortRemote = useCallback(
    (
      selectedColumn: TableColumn<IActivitiesTabs>,
      sortDirection: SortOrder,
    ) => {
      changePaginationMetadata(
        'orderColumn',
        selectedColumn.sortField as unknown as ORDER_COLUMN,
      );
      changePaginationMetadata(
        'sortDirection',
        sortDirection.toUpperCase() as ORDER_TYPE,
      );
      changePaginationMetadata('selectedPage', 0);
    },
    [],
  );

  const handlePageChange = useCallback((page: number) => {
    changePaginationMetadata('selectedPage', page - 1);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    changePaginationMetadata('rowsPerPage', rowsPerPage);
  }, []);

  const { dashboardService } = useService();

  const requestGetAllActivitiesByStatus = useCallback(
    (params: GetAllActivitiesByStatus.Params) => {
      setListStatusPage(STATUSPAGE.LOADING);
      return dashboardService
        .getAllActivitiesByStatus(params)
        .then((res) => {
          setActivityByStatusData(res);
        })
        .catch((err) => console.log(err))
        .finally(() => {
          setListStatusPage(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllActivities = useCallback(
    (valuesToSubmit: GetAllActivitiesByStatus.Params) =>
      requestGetAllActivitiesByStatus(valuesToSubmit),
    [],
  );

  const handleSearchRemote = useMemo(
    () =>
      debounce((searchString: string) => {
        changePaginationMetadata('searchString', searchString);
      }, DEFAULT_DEBOUNCE_DELAY),
    [],
  );

  const valuesToSubmitActivitiesAllByStatus = {
    length: String(paginationMetadata.rowsPerPage),
    profileType: '2',
    orderColumn: ORDER_COLUMN.STUDENT,
    order: ORDER_TYPE.ASC,
    status: activityStatus,
    query: paginationMetadata.searchString,
    group: filters?.group?.value,
    entity: filters?.entity?.value,
    course: filters?.course?.value,
    semester: filters?.semester?.value,
    page: String(paginationMetadata.selectedPage),
    curricularUnit: filters?.curricularUnit?.value,
    starts: filters?.starts,
    ends: filters?.ends,
  };

  useEffect(() => {
    if (!filters) return;
    requestGetAllActivities({
      ...valuesToSubmitActivitiesAllByStatus,
    });
  }, [paginationMetadata, filters]);

  return (
    <>
      <DataTable
        noDataBg={noDataBg}
        loading={listStatusPage === STATUSPAGE.LOADING}
        actions={[
          {
            id: 1,
            name: 'view',
            label: 'Visualizar detalhes da atividade',
            icon: 'ri-file-search-line',
            classNameColorIcon: 'none',
            onPress: (cellProps) => {
              requestGetActivityByStatus({
                id: cellProps?.id,
              });
              toggleModal();
            },
          },
        ]}
        canSearch
        pagination
        sortServer
        searchServer
        paginationServer
        data={activityByStatusData?.activities || []}
        onSort={handleSortRemote}
        onSearch={(e) => {
          setListStatusPage(STATUSPAGE.LOADING);
          handleSearchRemote(e);
        }}
        onChangePage={handlePageChange}
        columns={ColumnsActivities[activityStatus]()}
        paginationTotalRows={activityByStatusData?.total}
        onChangeRowsPerPage={handleRowsPerPageChange}
      />

      <ModalActivityDetails
        isOpen={isModalOpen}
        toggle={toggleModal}
        activity={activityByStatus}
        loading={loadingActivityByStatus === STATUSPAGE.LOADING}
      />
    </>
  );
};

export default TableActivityByStatus;
